import { observeTelemetrySessionPrivacy } from "./telemetrySessionPrivacy";

const STAGED_ACCESS_CODE_KEY = "fairpath_guest_access_code";
const STAGED_LEGACY_TOKEN_KEY = "fairpath_guest_legacy_url_token";
const PERSISTED_GUEST_TOKEN_KEY = "fairpath_guest_token";

export type GuestAccessUrlCapture = {
  accessCode: string | null;
  legacySessionToken: string | null;
  sanitizedUrl: string;
};

export function redactGuestAccessUrl(urlValue: string): GuestAccessUrlCapture {
  const url = new URL(urlValue);
  const accessCode = url.searchParams.get("access_code");
  const resumeValue = url.searchParams.get("resume");
  const legacySessionToken =
    url.searchParams.get("session_token") ??
    (resumeValue && resumeValue !== "true" ? resumeValue : null);

  url.searchParams.delete("access_code");
  url.searchParams.delete("session_token");
  if (legacySessionToken === resumeValue) {
    url.searchParams.delete("resume");
  }

  return {
    accessCode,
    legacySessionToken,
    sanitizedUrl: url.toString(),
  };
}

export function captureGuestAccessFromCurrentUrl(): void {
  if (typeof window === "undefined") return;
  observeTelemetrySessionPrivacy();
  const capture = redactGuestAccessUrl(window.location.href);
  try {
    if (capture.accessCode) {
      sessionStorage.setItem(STAGED_ACCESS_CODE_KEY, capture.accessCode);
    }
    if (capture.legacySessionToken) {
      sessionStorage.setItem(
        STAGED_LEGACY_TOKEN_KEY,
        capture.legacySessionToken,
      );
    }
  } catch {
    // Unavailable storage must not stop startup or leave credentials in the URL.
  } finally {
    if (capture.sanitizedUrl !== window.location.href) {
      window.history.replaceState(
        window.history.state,
        "",
        capture.sanitizedUrl,
      );
    }
  }
}

export function getStagedGuestAccessCode(): string | null {
  try {
    return sessionStorage.getItem(STAGED_ACCESS_CODE_KEY);
  } catch {
    return null;
  }
}

export function clearStagedGuestAccessCode(): void {
  try {
    sessionStorage.removeItem(STAGED_ACCESS_CODE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

export function getStagedLegacyGuestToken(): string | null {
  try {
    return sessionStorage.getItem(STAGED_LEGACY_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearStagedLegacyGuestToken(): void {
  try {
    sessionStorage.removeItem(STAGED_LEGACY_TOKEN_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

export function getPersistedGuestToken(): string | null {
  try {
    const sessionToken = sessionStorage.getItem(PERSISTED_GUEST_TOKEN_KEY);
    if (sessionToken) return sessionToken;
  } catch {
    // Continue to the durable fallback when session storage is unavailable.
  }
  try {
    return localStorage.getItem(PERSISTED_GUEST_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Removes one specific guest session token from storage, wherever it is the
 * stored value, and leaves any other token alone.
 *
 * Checkout calls this when the server refuses a repeat purchase and states
 * that the token it was sent owns no packet (FAI-197). Left in place, that
 * token held /guest/packet on a payment that never happened. A different
 * token held in the other store (an owner's paid session from an earlier
 * visit, say) is untouched.
 */
export function clearRefusedGuestToken(token: string): void {
  for (const store of [() => sessionStorage, () => localStorage]) {
    try {
      const storage = store();
      if (storage.getItem(PERSISTED_GUEST_TOKEN_KEY) === token) {
        storage.removeItem(PERSISTED_GUEST_TOKEN_KEY);
      }
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }
}

/** Router state that asks /guest/packet for its emailed-link recovery form. */
export const PRIOR_PURCHASE_RECOVERY_STATE = { priorPurchaseRecovery: true } as const;

export function isPriorPurchaseRecoveryState(state: unknown): boolean {
  return typeof state === "object" && state !== null &&
    (state as { priorPurchaseRecovery?: unknown }).priorPurchaseRecovery === true;
}

export function persistGuestToken(token: string): void {
  try {
    sessionStorage.setItem(PERSISTED_GUEST_TOKEN_KEY, token);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
  try {
    localStorage.setItem(PERSISTED_GUEST_TOKEN_KEY, token);
  } catch {
    // The in-memory React state still permits access for the current page.
  }
}
